import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, FileText, ArrowRight, AlertTriangle } from 'lucide-react';
import { FadeIn, SectionLabel } from '../components/Shared';
import ShieldOrbitBg from '../components/bg/ShieldOrbitBg';

const STATS = [
    { value: '43%', label: 'of attacks target SMBs' },
    { value: '£3,400', label: 'avg. cost of a breach' },
    { value: '94%', label: 'malware delivered via web' },
    { value: '60%', label: 'close within 6 months of breach' },
];

const Security = () => {
    return (
        <main className="bg-white text-[#1A1A2E] pb-24" data-testid="security-page">
            {/* Hero */}
            <section className="relative pt-36 pb-20 overflow-hidden" data-testid="security-hero">
                <div className="absolute inset-0 z-0"><ShieldOrbitBg /></div>
                <div className="absolute inset-0 hero-aura pointer-events-none z-[1]" />
                <div className="absolute inset-0 grid-pattern-light opacity-30 [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)] z-[1]" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-7">
                        <SectionLabel>AI Security</SectionLabel>
                        <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.1] text-[#1A1A2E] text-balance heading-glow">
                            Website security,
                            <br />
                            <span className="text-[#00B89E]">simplified.</span>
                        </h1>
                        <p className="mt-6 text-[17px] text-[#4A4A5A] max-w-2xl">
                            AI-powered protection for your website. Every month. Every
                            vulnerability. Covered.
                        </p>
                    </div>
                    <div className="md:col-span-5" />
                </div>
            </section>

            {/* Problem */}
            <section className="py-24 bg-[#F7F7F8]" data-testid="security-problem">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>The problem</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] max-w-4xl leading-[1.15]">
                            Why your website needs monthly security.
                        </h2>
                        <p className="mt-5 text-[17px] text-[#4A4A5A] max-w-3xl leading-relaxed">
                            43% of cyber attacks target small businesses. Most website owners
                            don't know their site has vulnerabilities until it's too late — data
                            breaches, SEO penalties, lost customer trust, and expensive
                            emergency fixes. Our monthly security service catches problems
                            before they become disasters.
                        </p>
                    </FadeIn>

                    <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {STATS.map((s, i) => (
                            <FadeIn key={i} delay={i * 0.08}>
                                <div
                                    className="bg-white border border-[#E8E8EC] rounded-2xl p-6 h-full"
                                    data-testid={`security-stat-${i + 1}`}
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <AlertTriangle className="w-4 h-4 text-[#7B61FF]" />
                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6B6B7A]">
                                            Threat
                                        </span>
                                    </div>
                                    <div className="font-display text-4xl sm:text-5xl font-semibold text-[#1A1A2E] tracking-tight">
                                        {s.value}
                                    </div>
                                    <div className="mt-2 text-[14px] text-[#4A4A5A] leading-snug">
                                        {s.label}
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* Solution Cards */}
            <section className="py-24 bg-white" data-testid="security-solution">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>The solution</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] max-w-4xl leading-[1.15]">
                            How we keep you <span className="text-[#00B89E]">protected.</span>
                        </h2>
                    </FadeIn>

                    <div className="mt-14 space-y-5">
                        {[
                            {
                                icon: ShieldCheck,
                                title: 'AI Code Audit',
                                intro: "Our advanced AI analyses every line of your website's code monthly, checking for:",
                                items: [
                                    'Cross-Site Scripting (XSS) vulnerabilities',
                                    'SQL Injection risks',
                                    'Cross-Site Request Forgery (CSRF)',
                                    'Insecure data exposure',
                                    'Broken authentication patterns',
                                    'Outdated or vulnerable dependencies',
                                ],
                                outro: 'Each vulnerability is classified by severity (Critical / High / Medium / Low) and our team implements the fixes.',
                            },
                            {
                                icon: Lock,
                                title: 'SSL & Headers Monitoring',
                                intro: 'We continuously verify:',
                                items: [
                                    'SSL/TLS certificate validity and expiry',
                                    'Content-Security-Policy (CSP) headers',
                                    'HTTP Strict Transport Security (HSTS)',
                                    'X-Frame-Options',
                                    'X-Content-Type-Options',
                                    'Referrer-Policy',
                                ],
                                outro: 'Misconfigured headers are one of the most common — and most preventable — security issues.',
                            },
                            {
                                icon: FileText,
                                title: 'Monthly Report & Updates',
                                intro: 'Every month you receive:',
                                items: [
                                    'Security risk score (0-100) with trend over time',
                                    'Full list of checks performed',
                                    'Vulnerabilities found and remediated',
                                    'Plugin/dependency updates applied',
                                    'GDPR compliance status',
                                    'Recommendations for the next month',
                                ],
                                outro: 'Professional, clear, actionable — delivered to your inbox as a PDF.',
                            },
                        ].map((card, i) => (
                            <FadeIn key={i} delay={i * 0.1}>
                                <div
                                    className="grid grid-cols-1 md:grid-cols-12 gap-8 p-8 md:p-10 bg-white border border-[#E8E8EC] rounded-3xl hover:shadow-card-hover transition-all duration-500"
                                    data-testid={`security-solution-card-${i + 1}`}
                                >
                                    <div className="md:col-span-3 flex md:flex-col items-center md:items-start gap-4">
                                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#E6FBF6]">
                                            <card.icon className="w-6 h-6 text-[#00B89E]" />
                                        </div>
                                        <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-[#1A1A2E]">
                                            {card.title}
                                        </h3>
                                    </div>
                                    <div className="md:col-span-9">
                                        <p className="text-[#1A1A2E] leading-relaxed text-[16px]">{card.intro}</p>
                                        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                            {card.items.map((it, j) => (
                                                <li key={j} className="flex items-start gap-2.5 text-[14px] text-[#4A4A5A]">
                                                    <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#00B89E] shrink-0" />
                                                    <span>{it}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <p className="mt-5 text-[14px] text-[#6B6B7A] italic leading-relaxed">
                                            {card.outro}
                                        </p>
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Reminder */}
            <section className="py-24 bg-[#F7F7F8]" data-testid="security-pricing-reminder">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <FadeIn>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] text-balance leading-[1.15]">
                            All of this for just <span className="text-[#00B89E]">£49/month.</span>
                        </h2>
                        <p className="mt-5 text-[17px] text-[#4A4A5A]">
                            First month free with any web development package. Cancel anytime.
                        </p>
                        <div className="mt-9 flex justify-center">
                            <Link
                                to="/contact?plan=security"
                                className="inline-flex items-center gap-2 bg-[#1A1A2E] text-white font-semibold text-[15px] px-7 py-3.5 rounded-full hover:bg-[#00B89E] transition-all duration-300"
                                data-testid="security-cta-protect"
                            >
                                Protect Your Website Now <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Security;
